<template>
  <div class="training-section-wrapper">
    <!-- Section Header Skeleton -->
    <div class="section-header-custom">
      <div class="section-header-left">
        <div class="skeleton-icon"></div>
        <div class="skeleton-title"></div>
      </div>
      <div class="section-header-right">
        <div class="skeleton-btn"></div>
      </div>
    </div>

    <!-- Content Grid Skeleton -->
    <div class="training-section-content">
      <div class="training-grid">
        <div v-for="i in cardCount" :key="'skeleton_card_' + i" class="training-grid-item">
          <div class="skeleton-card">
            <!-- Image -->
            <div class="skeleton-card-image"></div>
            <!-- Content -->
            <div class="skeleton-card-content">
              <!-- Meta: Category + Icon -->
              <div class="skeleton-card-meta">
                <div class="skeleton-card-category"></div>
                <div class="skeleton-card-icon"></div>
              </div>
              <!-- Title: 3 lines -->
              <div class="skeleton-card-title">
                <div class="skeleton-line"></div>
                <div class="skeleton-line"></div>
                <div class="skeleton-line short"></div>
              </div>
              <!-- Footer: Progress + Date -->
              <div class="skeleton-card-footer">
                <div class="skeleton-progress">
                  <div class="skeleton-progress-bar"></div>
                </div>
                <div class="skeleton-date">
                  <div class="skeleton-date-icon"></div>
                  <div class="skeleton-date-text"></div>
                </div>
              </div>
            </div>
            <!-- Rating Footer (optional) -->
            <div v-if="showRating" class="skeleton-card-rating">
              <div class="skeleton-rating-number"></div>
              <div class="skeleton-rating-stars"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr>
  </div>
</template>

<script>
export default {
  name: "TrainingSectionSkeleton",
  props: {
    cardCount: {
      type: Number,
      default: 4
    },
    showRating: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
/* Wrapper */
.training-section-wrapper {
  width: 100%;
}

/* Section Header */
.section-header-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.section-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.skeleton-icon {
  width: 28px;
  height: 28px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 10px;
}

.skeleton-title {
  width: 150px;
  height: 22px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 6px;
}

.section-header-right {
  flex-shrink: 0;
  margin-left: 12px;
}

.skeleton-btn {
  width: 80px;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 8px;
}

/* Content */
.training-section-content {
  width: 100%;
  overflow: hidden;
  padding-bottom: 8px;
}

/* Grid - matches TrainingSection exactly */
.training-grid {
  display: grid !important;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}

.training-grid-item {
  width: 100% !important;
  min-width: 0;
  padding: 0 !important;
  margin: 0 !important;
}

/* Skeleton Card - matches TrainingCard exactly */
.skeleton-card {
  background: #fff;
  border: none;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Image - 16:9 aspect ratio like TrainingCard */
.skeleton-card-image {
  width: 100%;
  aspect-ratio: 16/9;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

/* Content - matches TrainingCard padding */
.skeleton-card-content {
  padding: 14px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Meta */
.skeleton-card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.skeleton-card-category {
  width: 90px;
  height: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-card-icon {
  width: 16px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 50%;
}

/* Title - 3 lines like TrainingCard */
.skeleton-card-title {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.skeleton-line {
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
  width: 100%;
}

.skeleton-line.short {
  width: 60%;
}

/* Footer */
.skeleton-card-footer {
  margin-top: auto;
}

.skeleton-progress {
  height: 8px;
  background: #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
}

.skeleton-progress-bar {
  width: 45%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 8px;
}

.skeleton-date {
  display: flex;
  align-items: center;
  gap: 6px;
}

.skeleton-date-icon {
  width: 13px;
  height: 13px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 50%;
}

.skeleton-date-text {
  width: 100px;
  height: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

/* Rating Footer */
.skeleton-card-rating {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.skeleton-rating-number {
  width: 24px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-rating-stars {
  width: 90px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

/* Animation */
@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Large Desktop */
@media (min-width: 1200px) {
  .training-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
}

/* Desktop */
@media (max-width: 1199px) and (min-width: 961px) {
  .training-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
}

/* Tablet */
@media (max-width: 960px) {
  .training-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  .skeleton-icon {
    width: 24px;
    height: 24px;
    border-radius: 8px;
  }

  .skeleton-title {
    width: 120px;
    height: 20px;
  }

  .skeleton-btn {
    width: 50px;
    height: 28px;
  }

  .skeleton-card-content {
    padding: 12px;
    gap: 6px;
  }

  .skeleton-card-category {
    width: 70px;
    height: 11px;
  }

  .skeleton-card-icon {
    width: 14px;
    height: 14px;
  }

  .skeleton-line {
    height: 12px;
  }

  .skeleton-date-icon {
    width: 12px;
    height: 12px;
  }

  .skeleton-date-text {
    width: 90px;
    height: 11px;
  }

  .skeleton-card-rating {
    padding: 8px 12px;
    gap: 6px;
  }

  .skeleton-rating-number {
    width: 20px;
    height: 12px;
  }

  .skeleton-rating-stars {
    width: 80px;
    height: 12px;
  }
}

/* Small Tablet / Mobile */
@media (max-width: 768px) {
  .training-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .skeleton-icon {
    width: 22px;
    height: 22px;
    border-radius: 6px;
  }

  .skeleton-title {
    width: 100px;
    height: 18px;
  }

  .skeleton-btn {
    width: 45px;
    height: 26px;
    border-radius: 6px;
  }

  .skeleton-card {
    border-radius: 6px;
  }

  .skeleton-card-content {
    padding: 10px;
    gap: 5px;
  }

  .skeleton-card-category {
    width: 60px;
    height: 10px;
  }

  .skeleton-card-icon {
    width: 13px;
    height: 13px;
  }

  .skeleton-line {
    height: 11px;
    gap: 4px;
  }

  .skeleton-progress {
    height: 5px;
    margin-bottom: 8px;
  }

  .skeleton-date-icon {
    width: 11px;
    height: 11px;
  }

  .skeleton-date-text {
    width: 80px;
    height: 10px;
  }

  .skeleton-card-rating {
    padding: 8px 10px;
    gap: 5px;
  }

  .skeleton-rating-number {
    width: 18px;
    height: 10px;
  }

  .skeleton-rating-stars {
    width: 70px;
    height: 10px;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .training-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .skeleton-icon {
    width: 20px;
    height: 20px;
  }

  .skeleton-title {
    width: 80px;
    height: 16px;
  }

  .skeleton-btn {
    width: 40px;
    height: 24px;
  }

  .skeleton-card-content {
    padding: 8px;
    gap: 4px;
  }

  .skeleton-card-category {
    width: 50px;
    height: 9px;
  }

  .skeleton-card-icon {
    width: 12px;
    height: 12px;
  }

  .skeleton-line {
    height: 10px;
  }

  .skeleton-progress {
    height: 4px;
    margin-bottom: 6px;
  }

  .skeleton-date-icon {
    width: 10px;
    height: 10px;
  }

  .skeleton-date-text {
    width: 70px;
    height: 9px;
  }

  .skeleton-card-rating {
    padding: 6px 8px;
    gap: 4px;
  }

  .skeleton-rating-number {
    width: 16px;
    height: 9px;
  }

  .skeleton-rating-stars {
    width: 60px;
    height: 9px;
  }
}
</style>
